import {Component, Emitter, Environment, Object3D} from '@wonderlandengine/api';
import {ActuatorType, ButtplugBrowserWebsocketClientConnector, ButtplugClient, ButtplugClientDevice, ButtplugDeviceError, DeviceInfo, GenericDeviceMessageAttributes, MessageAttributes, ScalarSubcommand} from 'buttplug';
import { MenuController, Submenu } from './menus/menu-controller.js';
import { IntifaceRemoteRec } from './IntifaceRemoteRec.js';
import { IntifaceRemoteCon } from './IntifaceRemoteCon.js';
import { EventsClient } from './events-client.js';
import Logger from './utils/logger.js';

const MODULE = 'IntifaceController';

/**
 * Type alias for numeric peer IDs.
 * Peer IDs are assigned by the server after WebRTC connection is established.
 * Use this type instead of raw numbers to make peer identification explicit.
 */
export type PeerId = number;

/**
 * Key Terminology:
 * - **remoteKey**: Generic identifier that can be either:
 *   - A transient negotiation key (string) used during WebRTC connection setup
 *   - A stringified numeric peer ID after connection is established
 * - **peerKey**: Transient string key used during initial WebRTC offer/answer exchange
 * - **listenKey**: The transient key generated by receivers to identify themselves
 * - **peerId**: Numeric identifier assigned to established peer connections
 * 
 * Flow: Receiver generates listenKey → Controller uses as peerKey → After connection, use numeric peerId
 */

// Type definitions for event data structures
type ConnectEventData = {
    url?: string;
};

type DeviceCommandEventData = {
    deviceId: number;
    actuateType: SupportedAction;
    actuateVals: string[];
    duration?: string;
    modVals?: string[];
};

/**
 * Event data for commanding a remote device.
 * Uses numeric peer ID which is assigned after connection is established.
 */
type RemoteDeviceCommandEventData = DeviceCommandEventData & {
    peerId: PeerId;
};

/**
 * Event data for stopping device(s).
 * When peerId is provided, stops a specific remote device.
 * When peerId is undefined, stops local devices.
 */
type StopDeviceEventData = {
    deviceId: number;
    peerId?: PeerId;
};

/**
 * Event names for Intiface device control
 * Use these constants instead of string literals to prevent typos
 */
export const IntifaceEvents = {
    CONNECT_TO_INTIFACE: 'connect-to-intiface-central',
    DISCONNECT_FROM_INTIFACE: 'disconnect-from-intiface-central',
    SEND_DEVICE_COMMAND: 'send-intiface-device-command',
    STOP_DEVICE_COMMAND: 'stop-intiface-device-command',
    SEND_REMOTE_DEVICE_COMMAND: 'send-remote-device-command',
    STOP_REMOTE_DEVICE_COMMAND: 'stop-remote-device-command',
} as const;

/** Supported device actuation types for haptic control */
export enum SupportedAction {
    Vibrate = 'VIBRATE',
    Linear = 'LINEAR',
    Rotate = 'ROTATE',
    Oscillate = 'OSCILLATE',
    Scalar = 'SCALAR'
}

/** Array of all supported action types for iteration */
export const ALL_SUPPORTED_ACTIONS: SupportedAction[] = [
    SupportedAction.Vibrate,
    SupportedAction.Rotate,
    SupportedAction.Oscillate,
    SupportedAction.Linear,
    SupportedAction.Scalar
];

/** Device control modes determining who controls local devices */
export enum DeviceControlMode {
    /** Controlling own devices locally */
    Self = 'SELF',
    /** Remote peer controlling local devices */
    Receiver = 'RECEIVER',
    /** Environmental control of all devices */
    Environment = 'ENVIRONMENT'
}

/**
 * DeviceRemoteStub
 * Serializable representation of a Buttplug device for transmission over RPC.
 * Stores device capabilities and last command states for synchronization.
 */
export class DeviceRemoteStub {
    public index?: number;
    public name?: string;
    public messageAttributes?: MessageAttributes;
    public displayName?: string;
    public messageTimingGap?: number;
    public vibrateAttributes?: GenericDeviceMessageAttributes[] = [];
    public linearAttributes?: GenericDeviceMessageAttributes[] = [];
    public rotateAttributes?: GenericDeviceMessageAttributes[] = [];
    public oscillateAttributes?: GenericDeviceMessageAttributes[] = [];
    public lastVibrateCommand?: DeviceRemoteCommand | null = null;
    public lastLinearCommand?: DeviceRemoteCommand | null = null;
    public lastRotateCommand?: DeviceRemoteCommand | null = null;
    public lastOscillateCommand?: DeviceRemoteCommand | null = null;
    public lastScalarCommand?: DeviceRemoteCommand | null = null;

    constructor(data: Partial<DeviceRemoteStub>) {
        Object.assign(this, data);
    }

    /** Clear all stored last command states for this device */
    public nullifyLastCommands() {
        this.lastVibrateCommand = null;
        this.lastLinearCommand = null;
        this.lastRotateCommand = null;
        this.lastOscillateCommand = null;
        this.lastScalarCommand = null;
    }

    /**
     * Get the last command issued for a specific actuator type.
     * @param actuateType - The type of actuation to query
     * @returns The last command for that type, or null if none
     */
    public getLastCommand(actuateType: SupportedAction): DeviceRemoteCommand | null {
        switch (actuateType) {
            case SupportedAction.Vibrate:
                return this.lastVibrateCommand ?? null;
            case SupportedAction.Linear:
                return this.lastLinearCommand ?? null;
            case SupportedAction.Rotate:
                return this.lastRotateCommand ?? null;
            case SupportedAction.Oscillate:
                return this.lastOscillateCommand ?? null;
            case SupportedAction.Scalar:
                return this.lastScalarCommand ?? null;
        }
        return null;
    }

    /**
     * Store the last command issued for a specific actuator type.
     * @param actuateType - The type of actuation
     * @param command - The command to store, or null to clear
     */
    public setLastCommand(actuateType: SupportedAction, command:DeviceRemoteCommand | null) {
        switch (actuateType) {
            case SupportedAction.Vibrate:
                this.lastVibrateCommand = command;
                break;
            case SupportedAction.Linear:
                this.lastLinearCommand = command;
                break;
            case SupportedAction.Rotate:
                this.lastRotateCommand = command;
                break;
            case SupportedAction.Oscillate:
                this.lastOscillateCommand = command;
                break;
            case SupportedAction.Scalar:
                this.lastScalarCommand = command;
                break;
        }
    }

    /**
     * Convert this device stub to a plain JavaScript object for serialization.
     * @returns Plain object containing all device properties and states
     */
    // Method to get a plain data object
    toObject() {
        return {
            index: this.index,
            name: this.name,
            messageAttributes: this.messageAttributes,
            displayName: this.displayName,
            messageTimeGap: this.messageTimingGap,
            vibrateAttributes: this.vibrateAttributes,
            linearAttributes: this.linearAttributes,
            rotateAttributes: this.rotateAttributes,
            oscillateAttributes: this.oscillateAttributes,
            lastVibrateCommand: this.lastVibrateCommand,
            lastLinearCommand: this.lastLinearCommand,
            lastRotateCommand: this.lastRotateCommand,
            lastOscillateCommand: this.lastOscillateCommand,
            lastScalarCommand: this.lastScalarCommand
        };
    }

    /**
     * Create a DeviceRemoteStub instance from a plain JavaScript object.
     * @param data - Plain object containing device properties
     * @returns New DeviceRemoteStub instance
     */
    // Static method to create an instance from a plain data object
    static fromObject(data: {
        index: number,
        name: string,
        messageAttributes: MessageAttributes,
        displayName: string,
        messageTimingGap: number,
        vibrateAttributes: GenericDeviceMessageAttributes[],
        linearAttributes: GenericDeviceMessageAttributes[],
        rotateAttributes: GenericDeviceMessageAttributes[],
        oscillateAttributes: GenericDeviceMessageAttributes[],
        lastVibrateCommand: DeviceRemoteCommand,
        lastLinearCommand: DeviceRemoteCommand,
        lastRotateCommand: DeviceRemoteCommand,
        lastOscillateCommand: DeviceRemoteCommand,
        lastScalarCommand: DeviceRemoteCommand
    }): DeviceRemoteStub {
        return new DeviceRemoteStub({
            index: data.index,
            name: data.name,
            messageAttributes: data.messageAttributes,
            displayName: data.displayName,
            messageTimingGap: data.messageTimingGap,
            vibrateAttributes: data.vibrateAttributes,
            linearAttributes: data.linearAttributes,
            oscillateAttributes: data.oscillateAttributes,
            rotateAttributes: data.rotateAttributes,
            lastVibrateCommand: data.lastVibrateCommand,
            lastLinearCommand: data.lastLinearCommand,
            lastRotateCommand: data.lastRotateCommand,
            lastOscillateCommand: data.lastOscillateCommand,
            lastScalarCommand: data.lastScalarCommand
        });
    }
}

/**
 * DeviceRemoteCommand
 * Represents a command to be sent to a device, including actuation values and timing.
 */
export class DeviceRemoteCommand {
    public deviceIndex!: number; 
    public actuateType!: SupportedAction;
    public actuateVals!: string[];
    public duration?: string;
    public modVals?: string[];
    public startMs?: number;
    constructor(data: Partial<DeviceRemoteCommand>) {
        Object.assign(this, data);
    }
}

export class HapticEnvironment {
    private influenceMap: Map<SupportedAction, Map<Object3D, { magnitudes: number[]; expiresAt: number }>> = new Map();
    private logIntervalId: number | null = null;
    private lastLoggedInfluence: Map<SupportedAction, number[]> = new Map();

    constructor(data: Partial<HapticEnvironment>) {
        Object.assign(this, data);
        this.startEnvironmentInfluenceInterval();
    }

    public registerInfluence(source: Object3D, actType:SupportedAction, magnitudes:number[], durationMs:number) {
        const actionMap = this.influenceMap.get(actType) ?? new Map();
        const expiresAt = Date.now() + Math.max(0, durationMs);
        actionMap.set(source, { magnitudes, expiresAt });
        this.influenceMap.set(actType, actionMap);
        this.pruneExpired(actType);
    }

    public getInfluence(actType:SupportedAction):number[] {
        this.pruneExpired(actType);

        const actionMap = this.influenceMap.get(actType);
        if (!actionMap || actionMap.size === 0) {
            return [];
        }

        let maxLen = 0;
        for (const { magnitudes } of actionMap.values()) {
            if (magnitudes.length > maxLen) {
                maxLen = magnitudes.length;
            }
        }

        const totals = new Array(maxLen).fill(0);
        for (const { magnitudes } of actionMap.values()) {
            for (let i = 0; i < maxLen; i++) {
                const mag = Math.max(-1, Math.min(1, magnitudes[i] ?? 0));
                totals[i] += mag;
            }
        }

        for (let i = 0; i < totals.length; i++) {
            totals[i] = Math.min(1, totals[i]);
        }

        return totals;
    }

    public sendInfluenceCommandsIfEnvironmentActive() {
        const controller = IntifaceController.instance;
        if (!controller || controller.deviceControlMode !== DeviceControlMode.Environment) {
            return;
        }

        const clampInfluence = (val: number): number => {
            if (!Number.isFinite(val)) return 0;
            return Math.max(-1, Math.min(1, val ?? 0));
        };

        for (const action of ALL_SUPPORTED_ACTIONS) {
            const influence = this.getInfluence(action);
            const prev = this.lastLoggedInfluence.get(action);
            if (!prev || !this.arraysEqual(prev, influence)) {
                // Send commands to local devices
                for (const device of controller.getDevices()) {
                    if (device.index !== undefined && controller.hasActuateCapability(device, action)) {
                        const { influenceArray, optionalClockwiseArray } = this.buildInfluenceArrays(
                            controller, device, action, influence, clampInfluence
                        );
                        controller.sendDeviceCommand(device.index, action, influenceArray, undefined, optionalClockwiseArray);
                    }
                }

                // Send commands to remote devices
                for(const [peerId, peerMap] of controller.remoteDeviceMap.entries()) {
                    const remoteCon = controller.remoteConsByPeerId.get(peerId);
                    if(remoteCon) {
                        for (const device of peerMap.values()) {
                            if (device.index !== undefined && controller.hasActuateCapability(device, action)) {
                                const { influenceArray, optionalClockwiseArray } = this.buildInfluenceArrays(
                                    controller, device, action, influence, clampInfluence
                                );
                                remoteCon.sendRemoteDeviceCommand(remoteCon.peerID, device.index, action, influenceArray, undefined, optionalClockwiseArray);
                            }
                        }
                    }
                }

                this.lastLoggedInfluence.set(action, [...influence]);
            }
        }
    }

    /**
     * Build influence command arrays for a device.
     * Calculates normalized values and direction flags from environment influence data.
     * 
     * @param controller - IntifaceController instance
     * @param device - Target device (local or remote stub)
     * @param action - Action type (Vibrate, Rotate, etc.)
     * @param influence - Array of influence magnitudes to apply
     * @param clampInfluence - Function to clamp influence values to valid range
     * @returns Object with influenceArray and optionalClockwiseArray for device command
     */
    private buildInfluenceArrays(
        controller: IntifaceController,
        device: ButtplugClientDevice | DeviceRemoteStub,
        action: SupportedAction,
        influence: number[],
        clampInfluence: (val: number) => number
    ): { influenceArray: string[]; optionalClockwiseArray: string[] } {
        const numActuators = controller.numActuators(device, action);
        const maxValue = controller.maxActuatorValue(device, action);
        const influenceArray = new Array<string>(numActuators);
        const optionalClockwiseArray = new Array<string>(numActuators);

        for (let i = 0; i < numActuators; i++) {
            const sourceIndex = Math.min(i, influence.length - 1);
            const safeBase = clampInfluence(influence[sourceIndex]);
            const calculatedInfluence = safeBase * maxValue;

            influenceArray[i] = Math.abs(calculatedInfluence).toString();
            optionalClockwiseArray[i] = calculatedInfluence >= 0 ? 'true' : 'false';
        }

        return { influenceArray, optionalClockwiseArray };
    }

    private arraysEqual(a: number[], b: number[]): boolean {
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) return false;
        }
        return true;
    }

    private startEnvironmentInfluenceInterval() {
        if (this.logIntervalId !== null) {
            clearInterval(this.logIntervalId);
        }
        this.logIntervalId = window.setInterval(() => {
            this.sendInfluenceCommandsIfEnvironmentActive();
        }, 100);
    }

    private pruneExpired(actType: SupportedAction) {
        const actionMap = this.influenceMap.get(actType);
        if (!actionMap) {
            return;
        }

        const now = Date.now();
        for (const [source, payload] of actionMap) {
            if (payload.expiresAt <= now) {
                actionMap.delete(source);
            }
        }

        if (actionMap.size === 0) {
            this.influenceMap.delete(actType);
        }
    }
}


/**
 * IntifaceController
 * Singleton component managing Buttplug device connections and remote control.
 * Handles local device control, remote device sharing, and P2P device control via WebRTC.
 * 
 * **Architecture**: This class manages multiple concerns (organized into sections):
 * - **Singleton & Lifecycle**: Wonderland Engine Component integration
 * - **Local Device Management**: Buttplug/Intiface Central connection
 * - **Remote Connection Management**: WebRTC peer connections (receiver/controller roles)
 * - **Command Execution**: Actuator-specific device commands with timeout management
 * - **Device Capabilities & Utilities**: Device query/serialization helpers
 * - **UI Event Handling**: Menu system event dispatcher
 */
export class IntifaceController extends Component {
    static TypeName = 'IntifaceController';

    // ============================================================
    // STATE: LOCAL DEVICE MANAGEMENT
    // ============================================================
    /** Whether currently connected to Intiface Central */
    public connected: boolean = false;
    /** Whether connection attempt is in progress */
    public connecting: boolean = false;

    /** Buttplug client for local device communication */
    private bpClient!: ButtplugClient;
    /** Map of locally connected Buttplug devices */
    private devices: Map<number, ButtplugClientDevice> = new Map<number, ButtplugClientDevice>();
    /** Persist last command states for local devices to sync with controllers */
    public deviceLastCommandMap: Map<number, Map<SupportedAction,DeviceRemoteCommand | null>> = new Map<number, Map<SupportedAction, DeviceRemoteCommand | null>>();
    /** Track active timeouts for timed device commands */
    private deviceTimeoutMap: Map<number, Map<SupportedAction, number>> = new Map<number, Map<SupportedAction, number>>();
    /** Haptic environment for environmental influences on devices */
    public hapticEnvironment: HapticEnvironment = new HapticEnvironment({});

    // ============================================================
    // STATE: REMOTE CONNECTION MANAGEMENT
    // ============================================================
    /** Map of remote devices by peer ID, then by device index */
    public remoteDeviceMap: Map<number, Map<number, DeviceRemoteStub>> = new Map<number, Map<number, DeviceRemoteStub>>();
    /** Receiver instance for accepting remote control */
    private remoteRec!: IntifaceRemoteRec;
    /** Map of controller connections by numeric peer ID (primary identifier) */
    public remoteConsByPeerId: Map<number, IntifaceRemoteCon> = new Map<number, IntifaceRemoteCon>();
    /** Map of controller connections by temporary negotiation key (until peer ID assigned) */
    private remoteConsByKey: Map<string, IntifaceRemoteCon> = new Map<string, IntifaceRemoteCon>();
    /** Current device control mode (Self, Environment, or Receiver) */
    private _deviceControlMode: DeviceControlMode = DeviceControlMode.Self;

    // ============================================================
    // GETTERS: DEVICE CONTROL STATE
    // ============================================================
    /**
     * Gets the current device control mode.
     * @returns Current control mode (Self, Controller, or Receiver)
     */
    get deviceControlMode(): DeviceControlMode {
        return this._deviceControlMode;
    }
    /**
     * Gets the key that controllers use to connect to this receiver.
     * @returns Listen key string, or null if not in receiver mode
     */
    get listenKey(): string | null {
        if(this.remoteRec) {
            return this.remoteRec.listenKey;
        } else {
            return null;
        }
    }

    /**
     * Gets the peer ID of the controller currently controlling local devices.
     * @returns Numeric peer ID of controller, or null if no controller connected
     */
    get controllingPeerId(): number | null {
        if(this.remoteRec && this.remoteRec.controllingPeerId >= 0) {
            return this.remoteRec.controllingPeerId;
        } else {
            return null;
        }
    }

    /**
     * Disconnect from the remote receiver and return to Self control mode.
     */
    public async disconnectRemoteReceiver() {
        if (this.remoteRec) {
            await this.remoteRec.disconnect();
            this._deviceControlMode = DeviceControlMode.Self;
        }
    }

    // ============================================================
    // EVENTS: UI STATE CHANGE NOTIFICATIONS
    // ============================================================
    /**
     * Emitted when device list changes (local devices added/removed or remote device updates).
     * Listeners should refresh device control UI.
     */
    public onDevicesChanged: Emitter<[]> = new Emitter<[]>();

    /**
     * Emitted when connection state changes (connected/disconnected from Intiface Central).
     * Listeners should update connection UI and conditionals.
     */
    public onConnectionStateChanged: Emitter<[]> = new Emitter<[]>();

    /**
     * Emitted when device control mode changes (Self/Controller/Receiver).
     * Listeners should update UI conditionals and navigation.
     */
    public onControlModeChanged: Emitter<[]> = new Emitter<[]>();

    /**
     * Emitted when a remote peer connects or disconnects.
     * Payload: [peerKey: string, peerId: number | undefined, connected: boolean]
     * Listeners should update dynamic navigation elements.
     */
    public onRemotePeerChanged: Emitter<[string, number | undefined, boolean]> = new Emitter<[string, number | undefined, boolean]>();

    /**
     * Emitted when an error occurs that should be displayed to the user.
     * Payload: [errorMessage: string]
     */
    public onError: Emitter<[string]> = new Emitter<[string]>();

    // ============================================================
    // SECTION 1: SINGLETON & LIFECYCLE
    // ============================================================
    /** Singleton instance of IntifaceController */
    private static _instance: IntifaceController;
    /**
     * Gets the singleton instance of IntifaceController.
     * @returns The singleton instance
     */
    static get instance(): IntifaceController {
        return IntifaceController._instance;
    }

    /**
     * Get devices for display/control. Returns local devices by default,
     * or remote devices if remoteKey is provided.
     * @param remoteKey - Optional peer ID (number or string) to get remote devices
     * @returns Iterator of devices (local or remote)
     */
    // remoteKey may be either a peerKey string or a stringified numeric peerID. We prefer to return
    // devices keyed by numeric peerID (the authoritative identifier).
    /**
     * Get an iterator over devices for the current context.
     * Returns local devices by default, or remote devices if peerId is provided.
     * 
     * @param peerId - Optional numeric peer ID assigned after connection establishment
     * @returns Iterator over ButtplugClientDevice (local) or DeviceRemoteStub (remote) objects
     * 
     * @example
     * // Get local devices
     * for (const device of controller.getDevices()) {
     *   console.log(device.name);
     * }
     * 
     * @example
     * // Get remote devices by peer ID
     * for (const device of controller.getDevices(12345)) {
     *   console.log(device.name);
     * }
     */
    public getDevices(peerId?: PeerId): MapIterator<ButtplugClientDevice | DeviceRemoteStub> {
        Logger.debug(`Getting devices with peerId: ${peerId}`, MODULE);
        if (peerId !== undefined) {
            const remoteDevices = this.remoteDeviceMap.get(peerId);
            if (remoteDevices) {
                return remoteDevices.values();
            }
            Logger.warn(`No remote devices found for peer ID ${peerId}`, MODULE);
        }

        return this.devices.values();
    }

    /**
     * Get the current aggregated environment influence for an action type.
     * @param action - Supported action type
     * @returns Influence array in range [-1, 1] per actuator lane
     */
    public getInfluence(action: SupportedAction): number[] {
        return this.hapticEnvironment.getInfluence(action);
    }

    // ============================================================
    // SECTION 1: SINGLETON & LIFECYCLE
    // ============================================================
    init() {
        if (IntifaceController._instance) {
            Logger.error('There can only be one instance of IntifaceController Component', MODULE);
        } else {
            IntifaceController._instance = this;
        }
    }

    start() {
        MenuController.instance.onUserChoice.add(this.onUserChoice.bind(this));
    }

    // ============================================================
    // SECTION 2: UI EVENT HANDLING
    // ============================================================
    /**
     * Handle user interaction events from the menu system.
     * Dispatches to appropriate handler based on event type.
     * 
     * @param eventType - Event identifier from IntifaceEvents constants
     * @param eventData - Type-specific event payload (ConnectEventData, DeviceCommandEventData, etc.)
     * 
     * @remarks
     * This method acts as the main event dispatcher for all Intiface-related actions.
     * Event data is type-asserted based on eventType to ensure type safety.
     * 
     * Supported event types:
     * - CONNECT_TO_INTIFACE: Establish connection to Intiface Central
     * - DISCONNECT_FROM_INTIFACE: Close connection to Intiface Central
     * - SEND_DEVICE_COMMAND: Send command to local device
     * - STOP_DEVICE_COMMAND: Stop local or remote device
     * - SEND_REMOTE_DEVICE_COMMAND: Send command to remote peer's device
     * - STOP_REMOTE_DEVICE_COMMAND: Stop remote peer's device
     */
    onUserChoice (eventType : string, eventData : object | undefined) {
        switch (eventType) {
            case IntifaceEvents.CONNECT_TO_INTIFACE:
                if(!this.connecting && !this.connected) {
                    const data = eventData as ConnectEventData | undefined;
                    const url = data?.url || "ws://127.0.0.1:12345";
                    this.connectButtplug(url);
                }
                break;
            case IntifaceEvents.DISCONNECT_FROM_INTIFACE:
                if(!this.connecting) {
                    this.disconnectButtplug();
                }
                break;
            case IntifaceEvents.SEND_DEVICE_COMMAND:
                if(this.connected) {
                    const data = eventData as DeviceCommandEventData;
                    this.sendDeviceCommand(data.deviceId, data.actuateType, data.actuateVals, data.duration, data.modVals);
                }
                break;
            case IntifaceEvents.STOP_DEVICE_COMMAND:
                if (this.connected) {
                    const data = eventData as StopDeviceEventData;

                    this.stopDevice(data.deviceId);
                    this.onDevicesChanged.notify();
                }
                break;
            case IntifaceEvents.SEND_REMOTE_DEVICE_COMMAND:
                const sendData = eventData as RemoteDeviceCommandEventData;

                const remoteCon = this.remoteConsByPeerId.get(sendData.peerId);
                if (remoteCon) {
                    remoteCon.sendRemoteDeviceCommand(sendData.peerId, sendData.deviceId, sendData.actuateType, sendData.actuateVals, sendData.duration, sendData.modVals);
                } else {
                    Logger.error(`Remote connection not found for peerId: ${sendData.peerId}`, MODULE);
                }
                break;
            case IntifaceEvents.STOP_REMOTE_DEVICE_COMMAND:
                const stopData = eventData as StopDeviceEventData;
                
                if (stopData.peerId !== undefined) {
                    const remCon = this.remoteConsByPeerId.get(stopData.peerId);
                    if (remCon) {
                        remCon.sendStopRemoteDevice(stopData.peerId, stopData.deviceId);
                    } else {
                        Logger.error(`Remote connection not found for peerId: ${stopData.peerId}`, MODULE);
                    }
                } else {
                    Logger.error(`No peer ID provided for remote device stop command`, MODULE);
                }
                break;
        }
    }

    // ============================================================
    // SECTION 3: COMMAND EXECUTION
    // ============================================================
    /**
     * Setup and manage timeout for device commands.
     * Clears any existing timeout for the action and creates a new one if duration is specified.
     * @param device - The device to control
     * @param deviceIndex - Index of the device
     * @param action - Type of actuation
     * @param duration - Optional duration in seconds
     * @param onComplete - Optional callback invoked when timed command completes
     */
    private setupCommandTimeout(
        device: ButtplugClientDevice,
        deviceIndex: number,
        action: SupportedAction,
        duration: string | undefined,
        onComplete?: () => void
    ): void {
        // Ensure timeout map exists for this device
        let timeoutMap = this.deviceTimeoutMap.get(deviceIndex);
        if (!timeoutMap) {
            timeoutMap = new Map<SupportedAction, number>();
            this.deviceTimeoutMap.set(deviceIndex, timeoutMap);
        }

        // Clear any existing timeout for this device/action
        const existingTimeout = timeoutMap?.get(action);
        if (existingTimeout) {
            clearTimeout(existingTimeout);
        }

        // Setup new timeout if duration is valid
        if (duration && !isNaN(parseFloat(duration)) && parseFloat(duration) > 0) {
            const timeoutId = window.setTimeout(async () => {
                await device.stop();
                const tempMap = this.deviceLastCommandMap.get(deviceIndex);
                if (tempMap) {
                    for (const key of tempMap.keys()) {
                        tempMap.set(key, null);
                    }
                }
                // Remove this timeout from tracking
                this.deviceTimeoutMap.get(deviceIndex)?.delete(action);
                this.onDevicesChanged.notify();
                // Notify completion (for remote device updates)
                if (onComplete) onComplete();
            }, parseFloat(duration) * 1000);

            // Store the timeout handle
            const timeoutMap = this.deviceTimeoutMap.get(deviceIndex);
            if (timeoutMap) {
                timeoutMap.set(action, timeoutId);
            }
        }
    }

    /**
     * Normalizes a step count value (0 to StepCount) to Buttplug's expected range (0.0 to 1.0).
     * Buttplug devices expose their capabilities in discrete steps, but commands use normalized values.
     * @param value - The step count value from user input
     * @param maxSteps - Maximum step count for this actuator
     * @returns Normalized value between 0.0 and 1.0
     */
    private normalizeStepValue(value: string, maxSteps: number): number {
        return parseFloat(value) / maxSteps;
    }

    /**
     * Stop a local device and clear all active timeouts.
     * Clears all timeout handles and resets last command state for the device.
     * 
     * @param deviceIndex - Index of the device to stop
     * 
     * @remarks
     * - Clears all active timeouts for the device (vibrate, rotate, linear, oscillate, scalar)
     * - Resets all last command states to null
     * - Sends stop command to physical device via Buttplug protocol
     * - If device is not found, operation is silently skipped
     * 
     * @example
     * // Stop device at index 0
     * await IntifaceController.instance.stopDevice(0);
     */
    async stopDevice(deviceIndex : number) {
        const device = this.devices.get(deviceIndex);
        if (!device) return;

        // Clear any active timeouts for this device
        const timeoutMap = this.deviceTimeoutMap.get(deviceIndex);
        if (timeoutMap) {
            for (const [action, timeoutId] of timeoutMap.entries()) {
                clearTimeout(timeoutId);
                Logger.debug(`Cleared timeout for device ${deviceIndex} action ${action}`, MODULE);
            }
            timeoutMap.clear();
        }
        
        // Clear last command states by recreating the map
        const commandMap = this.deviceLastCommandMap.get(deviceIndex);
        if (commandMap) {
            commandMap.clear();
        }
        
        await device.stop();
    }

    /**
     * Send a command to a local device. Supports timed commands with optional completion callback.
     * @param deviceIndex - Index of the device to control
     * @param actuateType - Type of actuation (vibrate, rotate, linear, etc.)
     * @param actuateVals - Array of actuation values as strings
     * @param duration - Optional duration in seconds
     * @param modVals - Optional modifier values (e.g., rotation direction)
     * @param onComplete - Optional callback invoked when timed command completes
     */
    // duration is optional; commands remain until an overriding command or explicit stop
    // onComplete callback is called when a timed command finishes (optional, used for remote device notifications)
    async sendDeviceCommand(deviceIndex : number, actuateType: SupportedAction, actuateVals : string[], duration?: string, modVals? : string[], onComplete?: () => void) {
        // Validate device exists before proceeding
        const device = this.devices.get(deviceIndex);
        if (!device) {
            const errorMsg = `Device ${deviceIndex} not found or disconnected`;
            Logger.error(errorMsg, MODULE);
            this.onError.notify(errorMsg);
            return;
        }

        // Validate actuation values array is not empty
        if (!actuateVals || actuateVals.length === 0) {
            const errorMsg = `No actuation values provided for device ${deviceIndex}`;
            Logger.error(errorMsg, MODULE);
            this.onError.notify(errorMsg);
            return;
        }

        // Track the last command issued for this device (local devices only)
        const lastCommand = this.createRemoteCommand(deviceIndex, actuateType, actuateVals, duration, modVals);
        let commandMap = this.deviceLastCommandMap.get(deviceIndex);
        if (commandMap) {
            commandMap.set(actuateType, lastCommand);
        } else {
            commandMap = new Map<SupportedAction, DeviceRemoteCommand | null>([[actuateType, lastCommand]]);
            this.deviceLastCommandMap.set(deviceIndex, commandMap);
        }

        // Execute the appropriate command based on actuateType
        switch (actuateType) {
            case SupportedAction.Vibrate:
                await this.executeVibrateCommand(device, deviceIndex, actuateVals, duration, onComplete);
                break;
            case SupportedAction.Rotate:
                await this.executeRotateCommand(device, deviceIndex, actuateVals, modVals, duration, onComplete);
                break;
            case SupportedAction.Linear:
                await this.executeLinearCommand(device, deviceIndex, actuateVals, duration, onComplete);
                break;
            case SupportedAction.Oscillate:
                await this.executeOscillateCommand(device, deviceIndex, actuateVals, duration, onComplete);
                break;
            case SupportedAction.Scalar:
                await this.executeScalarCommand(device, deviceIndex, actuateVals, duration, onComplete);
                break;
        }
    }

    /**
     * Normalize and build an array of actuator values from string input.
     * Converts string values to normalized floating point based on device step counts.
     * 
     * @param device - The device (used to get step count per actuator)
     * @param actuateVals - String array of raw values from user input
     * @param attributes - Device attributes array (vibrateAttributes, rotateAttributes, etc.)
     * @returns Normalized number array
     */
    private normalizeActuatorValues(
        actuateVals: string[],
        attributes: Array<{ StepCount: number }> | undefined
    ): number[] {
        const result: number[] = [];
        if (!attributes) return result;
        
        for (let i = 0; i < actuateVals.length; i++) {
            const maxSteps = attributes[i]?.StepCount ?? 1;
            result[i] = this.normalizeStepValue(actuateVals[i], maxSteps);
        }
        return result;
    }

    /**
     * Execute a vibrate command on a device.
     */
    private async executeVibrateCommand(device: ButtplugClientDevice, deviceIndex: number, actuateVals: string[], duration?: string, onComplete?: () => void): Promise<void> {
        const vnumArray = this.normalizeActuatorValues(actuateVals, device.vibrateAttributes);
        Logger.debug(`Vibrate command: device ${deviceIndex}, values: ${vnumArray}, duration: ${duration}s`, MODULE);
        
        this.setupCommandTimeout(device, deviceIndex, SupportedAction.Vibrate, duration, onComplete);
        await device.vibrate(vnumArray);
    }

    /**
     * Execute a rotate command on a device.
     */
    private async executeRotateCommand(device: ButtplugClientDevice, deviceIndex: number, actuateVals: string[], modVals: string[] | undefined, duration?: string, onComplete?: () => void): Promise<void> {
        const rnumArray: [number, boolean][] = [];
        const attributes = device.rotateAttributes;

        for (let i = 0; i < actuateVals.length; i++) {
            const maxSteps = attributes[i]?.StepCount ?? 1;
            const clockwise = (modVals && modVals.length > i) ? JSON.parse(modVals[i]) : true;
            rnumArray[i] = [this.normalizeStepValue(actuateVals[i], maxSteps), clockwise];
        }

        Logger.debug(`Rotate command: device ${deviceIndex}, values: ${rnumArray}, duration: ${duration}s`, MODULE);
        
        this.setupCommandTimeout(device, deviceIndex, SupportedAction.Rotate, duration, onComplete);
        await device.rotate(rnumArray);
    }

    /**
     * Execute a linear (stroking) command on a device.
     */
    private async executeLinearCommand(device: ButtplugClientDevice, deviceIndex: number, actuateVals: string[], duration?: string, onComplete?: () => void): Promise<void> {
        const lnumArray: [number, number][] = [];
        const attributes = device.linearAttributes;

        for (let i = 0; i < actuateVals.length; i++) {
            const maxSteps = attributes[i]?.StepCount ?? 1;
            lnumArray[i] = [this.normalizeStepValue(actuateVals[i], maxSteps), parseFloat(duration || "0")];
        }

        Logger.debug(`Linear command: device ${deviceIndex}, values: ${lnumArray}, duration: ${duration}s`, MODULE);
        
        this.setupCommandTimeout(device, deviceIndex, SupportedAction.Linear, duration, onComplete);
        await device.linear(lnumArray);
    }

    /**
     * Execute an oscillate command on a device.
     */
    private async executeOscillateCommand(device: ButtplugClientDevice, deviceIndex: number, actuateVals: string[], duration?: string, onComplete?: () => void): Promise<void> {
        const onumArray = this.normalizeActuatorValues(actuateVals, device.oscillateAttributes);
        Logger.debug(`Oscillate command: device ${deviceIndex}, values: ${onumArray}, duration: ${duration}s`, MODULE);
        
        this.setupCommandTimeout(device, deviceIndex, SupportedAction.Oscillate, duration, onComplete);
        await device.oscillate(onumArray);
    }

    /**
     * Execute a scalar command on a device.
     */
    private async executeScalarCommand(device: ButtplugClientDevice, deviceIndex: number, actuateVals: string[], duration?: string, onComplete?: () => void): Promise<void> {
        const scalarAttrs = this.getNonvibScalarAttributes(device);
        const snumArray = this.normalizeActuatorValues(actuateVals, scalarAttrs);
        
        if (scalarAttrs && snumArray.length > scalarAttrs.length) {
            throw new ButtplugDeviceError(
                `${snumArray.length} commands send to a device with ${scalarAttrs.length} actuators`
            );
        }

        const cmds: ScalarSubcommand[] = [];
        scalarAttrs.forEach((x, i) => {
            cmds.push(new ScalarSubcommand(x.Index, snumArray[i], x.ActuatorType));
        });

        Logger.debug(`Scalar command: device ${deviceIndex}, values: ${snumArray}, duration: ${duration}s`, MODULE);
        
        this.setupCommandTimeout(device, deviceIndex, SupportedAction.Scalar, duration, onComplete);
        await device.scalar(cmds);
    }

    /**
     * Get non-vibration scalar attributes for a device.
     * @param device - The device to query (local or remote stub)
     * @returns Array of scalar attributes excluding vibration actuators
     */
    public getNonvibScalarAttributes(device: ButtplugClientDevice | DeviceRemoteStub): GenericDeviceMessageAttributes[] {
        if(device.messageAttributes === undefined) {
            return [];
        } else {
            return (
                device.messageAttributes.ScalarCmd?.filter(
                    (x) => x.ActuatorType !== ActuatorType.Vibrate
                ) ?? []
            );
        }
    }

    // ============================================================
    // SECTION 4: LOCAL DEVICE MANAGEMENT
    // ============================================================
    /**
     * Disconnect from Intiface Central and stop device scanning.
     */
    async disconnectButtplug() {
        try {
            await this.bpClient.disconnect();
            this.connected = false;
            Logger.info('Intiface Client Disconnected', MODULE);
        } catch (e) {
            Logger.error('Exception attempting to disconnect from Intiface service', MODULE, e);
            this.connected = false;
        }
    }

    /**
     * Connect to Intiface Central and begin device scanning.
     * Sets up device event listeners and initiates automatic device discovery.
     * 
     * @param url - WebSocket URL of Intiface Central (default: ws://127.0.0.1:12345)
     * 
     * @remarks
     * Connection flow:
     * 1. Sets connecting flag and creates ButtplugClient instance
     * 2. Registers deviceadded/deviceremoved listeners
     * 3. Attempts connection via WebSocket connector
     * 4. On success: starts device scanning, updates UI, registers disconnect handler
     * 5. On failure: logs error, resets connection flags
     * 
     * Event listeners:
     * - deviceadded: Called when new device is detected, triggers UI update
     * - deviceremoved: Called when device disconnects, triggers UI update
     * - disconnect: Cleanup handler for unexpected connection loss
     * 
     * @throws Logs connection errors but does not throw (safe to call without try/catch)
     * 
     * @example
     * // Connect to default Intiface Central instance
     * await controller.connectButtplug("ws://127.0.0.1:12345");
     */
    async connectButtplug(url: string) {
        this.connecting = true;
        const connector = new ButtplugBrowserWebsocketClientConnector(url);
        this.bpClient = new ButtplugClient("Buttplug Example Client");

        this.bpClient.addListener("deviceadded", (device) => {
            Logger.info(`Device Connected: ${device.name}`, MODULE);
            if (Logger.isLevelEnabled(Logger.getLevel())) {
                this.bpClient.devices.forEach((device) => Logger.debug(`  - ${device.name}`, MODULE));
            }
            this.OnSelectedDevicesChange(this.bpClient.devices);
        });
        this.bpClient.addListener("deviceremoved", (device) => {
            Logger.info(`Device Removed: ${device.name}`, MODULE);
            if (Logger.isLevelEnabled(Logger.getLevel())) {
                this.bpClient.devices.forEach((device) => Logger.debug(`  - ${device.name}`, MODULE));
            }
            this.OnSelectedDevicesChange(this.bpClient.devices);
        });

        try {
            await this.bpClient.connect(connector);
            this.connected = true;
            this.connecting = false;
            Logger.info('Intiface Client Connected', MODULE);
            this.bpClient.addListener("disconnect", this.OnClientDisconnect.bind(this));
            this.bpClient.startScanning();
        } catch (e) {
            this.connected = false;
            this.connecting = false;
            Logger.error('Exception connecting to Intiface service', MODULE, e);
        }
    }

    update(dt: number) {
        /* Called every frame. */
    }

    private DisplayDeviceInfo() {
        if (!Logger.isLevelEnabled(Logger.getLevel()) || Logger.getLevel() > 0) {
            return; // Skip verbose device info unless debug level is enabled
        }
        
        for (const device of this.devices.values()) {
            Logger.debug(`Device: ${device.name} | ${device.displayName}`, MODULE);
            Logger.debug(`  canVibrate: ${this.hasActuateCapability(device, SupportedAction.Vibrate)}`, MODULE);
            Logger.debug(`  canRotate: ${this.hasActuateCapability(device, SupportedAction.Rotate)}`, MODULE);
            Logger.debug(`  canLinear: ${this.hasActuateCapability(device, SupportedAction.Linear)}`, MODULE);
            Logger.debug(`  numVibrators: ${this.getActuatorCount(device, SupportedAction.Vibrate)}`, MODULE);
            
            device.linearAttributes.forEach((x, i) => Logger.debug(`  linearAtt ${x.Index}: ${x.ActuatorType} (${x.StepCount} steps) - ${x.FeatureDescriptor}`, MODULE));
            device.rotateAttributes.forEach((x, i) => Logger.debug(`  rotateAtt ${x.Index}: ${x.ActuatorType} (${x.StepCount} steps) - ${x.FeatureDescriptor}`, MODULE));
            device.vibrateAttributes.forEach((x, i) => Logger.debug(`  vibrateAtt ${x.Index}: ${x.ActuatorType} (${x.StepCount} steps) - ${x.FeatureDescriptor}`, MODULE));
            device.oscillateAttributes.forEach((x, i) => Logger.debug(`  oscAtt ${x.Index}: ${x.ActuatorType} (${x.StepCount} steps) - ${x.FeatureDescriptor}`, MODULE));

            device.messageAttributes.ScalarCmd?.forEach((x, i) => Logger.debug(`  Scalar ${x.Index}: ${x.ActuatorType} (${x.StepCount} steps) - ${x.FeatureDescriptor}`, MODULE));
            device.messageAttributes.RotateCmd?.forEach((x, i) => Logger.debug(`  Rotate ${x.Index}: ${x.ActuatorType} (${x.StepCount} steps) - ${x.FeatureDescriptor}`, MODULE));
            device.messageAttributes.LinearCmd?.forEach((x, i) => Logger.debug(`  Linear ${x.Index}: ${x.ActuatorType} (${x.StepCount} steps) - ${x.FeatureDescriptor}`, MODULE));
            device.messageAttributes.SensorReadCmd?.forEach((x, i) => Logger.debug(`  Sensor ${x.Index}: ${x.SensorType} (${x.StepRange} range) - ${x.FeatureDescriptor}`, MODULE));
            device.messageAttributes.SensorSubscribeCmd?.forEach((x, i) => Logger.debug(`  SensorSub ${x.Index}: ${x.SensorType} (${x.StepRange} range) - ${x.FeatureDescriptor}`, MODULE));
        }
    }

    // ============================================================
    // SECTION 5: REMOTE CONNECTION MANAGEMENT
    // ============================================================
    /**
     * Enable receiver mode to allow a remote controller to control local devices.
     * Creates or reuses IntifaceRemoteRec instance and generates a temporary listen key.
     * 
     * @returns Promise resolving when listening is established and listen key is generated
     * 
     * @remarks
     * State transitions:
     * 1. Creates IntifaceRemoteRec instance if not already present
     * 2. Registers onDataChannelReady callback to sync device list when controller connects
     * 3. Starts listening for WebRTC offers with temporary key
     * 4. Sets device control mode to Receiver
     * 5. Returns listen key for sharing with potential controllers
     * 
     * The listen key should be shared with the remote user who wants to control your devices.
     * When they connect using this key, the onDataChannelReady callback sends them your device list.
     * 
     * @example
     * // Enable device sharing and get listen key
     * await controller.ShareDeviceControls();
     * // Share the listen key displayed in UI with remote controller
     */
    public async ShareDeviceControls() {
        if(!this.remoteRec) {
            this.remoteRec = new IntifaceRemoteRec();
            this.remoteRec.onDataChannelReady.add(() => {
                if (this.deviceControlMode == DeviceControlMode.Receiver) {
                    this.remoteRec.updateDevices(this.devices);
                    // Notify UI to show the connected controller's peer ID and update button visibility
                    this.onControlModeChanged.notify();
                    this.onDevicesChanged.notify();
                }
            });
        }
        this._deviceControlMode = DeviceControlMode.Receiver;
        this.onControlModeChanged.notify();
        // Preload TURN servers for receiver peer connections
        try {
            await EventsClient.instance?.loadTurnIceServers?.().catch((err) =>
                Logger.warn('Failed to load TURN servers for device control receiver', MODULE, err)
            );
            let listenKey = await this.remoteRec.listenForDeviceControlOffers();
            Logger.info(`Listening for device control offers with key: ${listenKey}`, MODULE);
        } catch (err) {
            this._deviceControlMode = DeviceControlMode.Self;
            this.onControlModeChanged.notify();
            throw err;
        }
    }

    /**
     * Connect to a remote receiver's devices in controller mode.
     * Establishes WebRTC connection using receiver's listen key and switches UI to remote device view.
     * 
     * @param remoteKey - Temporary negotiation key provided by receiver (from ShareDeviceControls)
     * 
     * @remarks
     * Connection flow:
     * 1. Checks for and cleans up any existing connection with same key
     * 2. Creates new IntifaceRemoteCon instance
     * 3. Stores transient mapping from negotiation key to connection
     * 4. Initiates WebRTC peer connection
     * 5. On success: switches to Controller mode, displays remote device UI
     * 6. On failure: shows error message, cleans up transient mapping
     * 
     * Once connected, remote peer's device list is received via data channel.
     * Connection is tracked by both negotiation key (temporary) and peer ID (permanent).
     * 
     * @example
     * // Connect to remote devices using receiver's listen key
     * await controller.ConnectRemoteDevices("abc123-temp-key");
     * // UI switches to show remote devices for control
     */
    public async ConnectRemoteDevices(remoteKey: string): Promise<boolean> {
        const trimmedKey = String(remoteKey ?? '').trim();
        if (!trimmedKey) {
            this.onError.notify('Please enter a valid remote key.');
            Logger.warn('Remote connection requested with empty key', MODULE);
            return false;
        }

        Logger.info(`Connecting to remote devices with key: ${trimmedKey}`, MODULE);

        // Check if we already have a connection for this key and clean it up
        const existingCon = this.remoteConsByKey.get(trimmedKey);
        if (existingCon) {
            Logger.debug('Cleaning up existing connection', MODULE);
            this.remoteConsByKey.delete(trimmedKey);
        }

        const con = new IntifaceRemoteCon();
        // Keep a transient mapping from the negotiation key to the connection until the peerID is known.
        this.remoteConsByKey.set(trimmedKey, con);

        try {
            // Preload TURN servers for controller peer connections
            await EventsClient.instance?.loadTurnIceServers?.().catch((err) =>
                Logger.warn('Failed to load TURN servers for device control controller', MODULE, err)
            );

            const connected = await con.connectToPeer(trimmedKey);
            if (connected) {
                this.onControlModeChanged.notify();
                this.onRemotePeerChanged.notify(trimmedKey, con.peerID, true);
                return true;
            }

            this.onError.notify('Could not connect to remote peer with key: ' + trimmedKey);
            Logger.error(`Could not connect to remote peer with key: ${trimmedKey}`, MODULE);
            // cleanup transient mapping
            this.remoteConsByKey.delete(trimmedKey);
            return false;
        } catch (err) {
            this.onError.notify('Could not connect to remote peer with key: ' + trimmedKey);
            Logger.error(`Remote connection attempt failed for key: ${trimmedKey}`, MODULE, err);
            this.remoteConsByKey.delete(trimmedKey);
            return false;
        }
    }

    private async OnClientDisconnect() {
        // Clear all active timeouts before clearing devices
        for (const [deviceIndex, timeoutMap] of this.deviceTimeoutMap.entries()) {
            for (const [action, timeoutId] of timeoutMap.entries()) {
                clearTimeout(timeoutId);
            }
        }
        this.deviceTimeoutMap.clear();
        
        this.devices = new Map<number, ButtplugClientDevice>();
        this.bpClient.removeListener("disconnect", this.OnClientDisconnect);
        this.connected =  false;
        this.onConnectionStateChanged.notify();
        this.onDevicesChanged.notify();
    }

    private OnSelectedDevicesChange(aDeviceList: ButtplugClientDevice[]) {
        Logger.debug(`Device list changed: ${aDeviceList.length} devices, mode: ${this.deviceControlMode}`, MODULE);
        this.devices = new Map<number, ButtplugClientDevice>();
        aDeviceList.forEach((value) => {
            this.devices.set(value.index, value);
        });

        if(this.deviceControlMode == DeviceControlMode.Receiver) {
            this.remoteRec.updateDevices(this.devices);
        }

        this.DisplayDeviceInfo();

        this.onDevicesChanged.notify();
    }

    // ============================================================
    // SECTION 6: DEVICE CAPABILITIES & UTILITIES
    // ============================================================
    public hasActuateCapability(device: ButtplugClientDevice | DeviceRemoteStub, action: SupportedAction): boolean {
        return this.getActuatorCount(device, action) > 0;
    }

    public numActuators(device: ButtplugClientDevice | DeviceRemoteStub, action: SupportedAction): number {
        return this.getActuatorCount(device, action);
    }

    public maxActuatorValue(device: ButtplugClientDevice | DeviceRemoteStub, action: SupportedAction, attributeIndex: number = 0): number {
        switch (action) {
            case SupportedAction.Vibrate:
                return this.getAttributeSteps(device.vibrateAttributes, attributeIndex);
            case SupportedAction.Rotate:
                return this.getAttributeSteps(device.rotateAttributes, attributeIndex); 
            case SupportedAction.Linear:
                return this.getAttributeSteps(device.linearAttributes, attributeIndex);
            default:
                return 0;
        }
    }

    /**
     * Check if a device has vibration capability.
     * @param device - Device to check (local or remote stub)
     * @returns True if device can vibrate
     */
    /**
     * Legacy aliases for hasActuateCapability. Use hasActuateCapability instead.
     */
    public canVibrate(device: ButtplugClientDevice | DeviceRemoteStub): boolean {
        return this.hasActuateCapability(device, SupportedAction.Vibrate);
    }

    public canRotate(device: ButtplugClientDevice | DeviceRemoteStub): boolean {
        return this.hasActuateCapability(device, SupportedAction.Rotate);
    }

    public canLinear(device: ButtplugClientDevice | DeviceRemoteStub): boolean {
        return this.hasActuateCapability(device, SupportedAction.Linear);
    }

    /**
     * Get the count of actuators for a specific action type.
     * @param device - Device to query
     * @param action - Action type (Vibrate, Rotate, Linear, Oscillate)
     * @returns Number of actuators available for the action, or 0 if not supported
     */
    public getActuatorCount(device: ButtplugClientDevice | DeviceRemoteStub, action: SupportedAction): number {
        if (!device) return 0;
        switch (action) {
            case SupportedAction.Vibrate:
                return device.vibrateAttributes?.length ?? 0;
            case SupportedAction.Rotate:
                return device.rotateAttributes?.length ?? 0;
            case SupportedAction.Linear:
                return device.linearAttributes?.length ?? 0;
            case SupportedAction.Oscillate:
                return device.oscillateAttributes?.length ?? 0;
            default:
                return 0;
        }
    }

    /**
     * @deprecated Use getActuatorCount(device, SupportedAction.Vibrate) instead
     */
    public numVibrators(device: ButtplugClientDevice | DeviceRemoteStub): number {
        return this.getActuatorCount(device, SupportedAction.Vibrate);
    }

    /**
     * @deprecated Use getActuatorCount(device, SupportedAction.Rotate) instead
     */
    public numRotators(device: ButtplugClientDevice | DeviceRemoteStub): number {
        return this.getActuatorCount(device, SupportedAction.Rotate);
    }

    /**
     * @deprecated Use getActuatorCount(device, SupportedAction.Linear) instead
     */
    public numLinears(device: ButtplugClientDevice | DeviceRemoteStub): number {
        return this.getActuatorCount(device, SupportedAction.Linear);
    }

    /**
     * Get step count for a specific device attribute (actuator).
     * Helper method to eliminate duplication across vibrate/rotate/scalar step getters.
     * 
     * @param attributes - Array of device attributes (vibrateAttributes, rotateAttributes, etc.)
     * @param attributeIndex - Index of the specific attribute to query (defaults to 0)
     * @returns Step count for the attribute, or 0 if attributes are undefined
     */
    private getAttributeSteps(
        attributes: Array<{ StepCount: number }> | undefined,
        attributeIndex: number = 0
    ): number {
        if (!attributes || attributes.length === 0) {
            return 0;
        }
        return attributes[attributeIndex].StepCount;
    }

    public getVibeSteps(device: ButtplugClientDevice | DeviceRemoteStub, attributeIndex?: number): number {
        return this.getAttributeSteps(device.vibrateAttributes, attributeIndex ?? 0);
    }

    public getRotateSteps(device: ButtplugClientDevice | DeviceRemoteStub, attributeIndex?: number): number {
        return this.getAttributeSteps(device.rotateAttributes, attributeIndex ?? 0);
    }

    public getScaleSteps(device: ButtplugClientDevice | DeviceRemoteStub, attributeIndex?: number): number {
        return this.getAttributeSteps(this.getNonvibScalarAttributes(device), attributeIndex ?? 0);
    }

    // Return all currently connected remote peer IDs (controller mode only)
    public getControllableUsers(): number[] {
        return Array.from(this.remoteConsByPeerId.keys());
    }

    /**
     * Returns the current map of locally connected devices.
     * Used by the remote-control sync path to capture a consistent snapshot.
     */
    public getLocalDevices(): Map<number, ButtplugClientDevice> {
        return this.devices;
    }

    /**
     * Serialize local devices to JSON for transmission to controller.
     * Includes current device states from deviceLastCommandMap.
     * @param devices - Map of local Buttplug devices
     * @returns JSON string representation of device stubs
     */
    public jsonifyDevices(devices: Map<number, ButtplugClientDevice>): string {
        let textDeviceArray : [number, DeviceRemoteStub][] = [];
        devices.forEach((value, key) => {
            let deviceInfo : DeviceRemoteStub = new DeviceRemoteStub({
                index: value.index,
                name: value.name,
                messageAttributes: value.messageAttributes,
                displayName: value.displayName,
                messageTimingGap: value.messageTimingGap,
                vibrateAttributes: value.vibrateAttributes,
                linearAttributes: value.linearAttributes,
                oscillateAttributes: value.oscillateAttributes,
                rotateAttributes: value.rotateAttributes,
            });
            
            // Include current actuator values from deviceLastCommandMap
            const commandMap = this.deviceLastCommandMap.get(key);
            if (commandMap) {
                deviceInfo.lastVibrateCommand = commandMap.get(SupportedAction.Vibrate) || null;
                deviceInfo.lastLinearCommand = commandMap.get(SupportedAction.Linear) || null;
                deviceInfo.lastRotateCommand = commandMap.get(SupportedAction.Rotate) || null;
                deviceInfo.lastOscillateCommand = commandMap.get(SupportedAction.Oscillate) || null;
                deviceInfo.lastScalarCommand = commandMap.get(SupportedAction.Scalar) || null;
            }
            
            textDeviceArray.push([key, deviceInfo]);
        });
        return JSON.stringify(textDeviceArray);
    }

    /**
     * Create a DeviceRemoteCommand object from command parameters.
     * @param deviceIndex - Index of the device
     * @param actuateType - Type of actuation
     * @param actuateVals - Array of actuation values
     * @param dur - Optional duration in seconds
     * @param modVals - Optional modifier values
     * @returns New DeviceRemoteCommand with timestamp
     */
    public createRemoteCommand(deviceIndex : number, actuateType: SupportedAction, actuateVals : string[], duration?: string, modVals? : string[]) : DeviceRemoteCommand{
        let command : DeviceRemoteCommand = new DeviceRemoteCommand({
            deviceIndex: deviceIndex,
            actuateType: actuateType,
            actuateVals: actuateVals,
            duration: duration,
            modVals: modVals,
            startMs: Date.now()
        });

        return command;
    }

    /**
     * Deserialize JSON device list from receiver into DeviceRemoteStub objects.
     * @param json - JSON string from receiver's jsonifyDevices method
     * @returns Map of device stubs keyed by device index
     */
    public devicifyJSON(json: string): Map<number, DeviceRemoteStub> {
        const devMap = new Map(JSON.parse(json));
        const newMap = new Map<number, DeviceRemoteStub>();
        for (const [key, value] of devMap.entries()) {
            // create real DeviceRemoteStub objects out of the plain data objects
            newMap.set(key as number, DeviceRemoteStub.fromObject(value as any));
        }
        return newMap;
    }

    /**
     * Called by IntifaceRemoteCon when numeric peer ID is assigned after connection.
     * Upgrades temporary peerKey mapping to stable peerID mapping.
     * @param peerKey - Temporary negotiation key
     * @param peerId - Numeric peer ID assigned by server
     */
    // Called by an IntifaceRemoteCon instance when the negotiated peerID is learned.
    // We prefer to key remote connections by numeric peerID (stable identifier).
    public onRemoteConnected(peerKey: string, peerId: number) {
        Logger.info(`Remote connected: peerKey=${peerKey} peerId=${peerId}`, MODULE);
        const con = this.remoteConsByKey.get(peerKey);
        if (con) {
            this.remoteConsByPeerId.set(peerId, con);
            //this.remoteConsByKey.delete(peerKey);
        } else {
            // If we don't have a transient mapping, just set the mapping by peerId.
            // This can happen if something else created the connection instance.
            Logger.warn(`No transient connection found for peerKey ${peerKey}; registering by peerId`, MODULE);
            // We don't have access to the IntifaceRemoteCon instance in this case.
        }
    }

    /**
     * Unregister a remote connection when it's closed or disconnected.
     * Cleans up both peerKey and peerID mappings, removes device map, updates UI.
     * @param peerKey - Temporary negotiation key used during connection
     * @param peerId - Optional numeric peer ID if known
     */
    public unregisterRemoteConnection(peerKey: string, peerId?: number) {
        Logger.info(`Unregistering remote connection: peerKey=${peerKey}, peerId=${peerId}`, MODULE);
        
        // Clean up by peerKey
        this.remoteConsByKey.delete(peerKey);
        
        // Clean up by peerID if available
        if (peerId !== undefined) {
            this.remoteConsByPeerId.delete(peerId);
            this.remoteDeviceMap.delete(peerId);
        }
        
        //this.onControlModeChanged.notify();
        // Notify UI about peer disconnection (submenu removal, navigation update)
        this.onRemotePeerChanged.notify(peerKey, peerId, false);
        this.onDevicesChanged.notify();
    }
    
    public unregisterRemoteByPeerId(peerId: number) {
        Logger.info(`Unregistering remote peer by ID: ${peerId}`, MODULE);
        this.remoteConsByPeerId.delete(peerId);
        this.remoteDeviceMap.delete(peerId);
        
        //this.onControlModeChanged.notify();
        // Notify UI about peer disconnection (submenu removal with numeric peer ID)
        this.onRemotePeerChanged.notify(peerId.toString(), peerId, false);
        this.onDevicesChanged.notify();
    }
}
